import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { UploadService } from "@merri/common";
import { Observable, Subject } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { LoggingService } from "src/app/core/services/logging.service";
import { UploadProgress } from "src/app/core/models/upload-progress";
import { UploadStatus } from "src/app/core/models/upload-status";
import { DownloadAllConfig } from "../download-all/models/download-all-config";
import { SkuColumn } from "./models/sku-column";
import { SkuHistory } from "./models/sku-history";
import { SkuHistoryResponse } from "./models/sku-history-response";

interface Template {
  fileUrl: string;
}

@Injectable()
export abstract class SkuManagementService {
  private fileUploadedSubject$: Subject<SkuHistory>;
  fileUploaded$: Observable<SkuHistory>;

  uploading: boolean = false;

  constructor(protected uploadService: UploadService, protected http: HttpClient) {
    LoggingService.log("base sku service created");
    this.fileUploadedSubject$ = new Subject<SkuHistory>();
    this.fileUploaded$ = this.fileUploadedSubject$.asObservable();
  }

  protected abstract getSkuHistory(response: any): SkuHistory;
  protected abstract getHistoryApiUrl(): string;
  protected abstract getTemplateApiUrl(): string;
  protected abstract getUploadApiUrl(): string;

  getDownloadAllConfig(): DownloadAllConfig {
    return null;
  }

  showGuidButton(): boolean {
    return true;
  }

  abstract getGuidButtonLabel(): string;
  abstract getTitle(): string;
  abstract getColumns(): SkuColumn[];

  getHistory$(): Observable<SkuHistory[]> {
    return this.http.get<SkuHistoryResponse>(this.getHistoryApiUrl()).pipe(
      map(data => data.rows.map(i => this.getSkuHistory(i))),
      catchError(err => {
        LoggingService.log(`SkuManagementService - getHistory error - ${JSON.stringify(err)}`);
        throw err;
      }),
    );
  }

  getTemplateLink$(): Observable<string> {
    return this.http.get<Template>(this.getTemplateApiUrl()).pipe(
      map(result => result.fileUrl),
      catchError(err => {
        LoggingService.log(`SkuManagementService - getTemplateLink error - ${JSON.stringify(err)}`);
        throw err;
      }),
    );
  }

  uploadFile$(file: any): Observable<UploadProgress> {
    this.uploading = true;

    return this.uploadService.uploadFile$<any>(file, this.getUploadApiUrl()).pipe(
      tap(progress => {
        if (progress.status == UploadStatus.Complete) {
          const skuHistory = this.getSkuHistory(progress.response.row);
          this.fileUploadedSubject$.next(skuHistory);
          this.uploading = false;
        }
      }),
      catchError(err => {
        this.uploading = false;
        LoggingService.log(`SkuManagementService - uploadFile error - ${JSON.stringify(err)}`);
        throw `Upload failed - ${err.status} ${err.statusText} - ${err.error.errMsgs}`;
      }),
    );
  }
}
